import type { ReactNode } from 'react';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useTranslation } from 'react-i18next';
import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { formatCivilDate, formatCurrency } from '@eis/core';
import { DetailsSection, ErrorState, StatusChip } from '@eis/patterns';
import { useCoverageAtDate } from '../api/policies';

/**
 * "Tarif & Deckung" on the policy overview (SDD-018 §7, prompt 26 phase 26.2): what the policy
 * covers at the page's cut — the variant its data selects, the categories it offers a claim under
 * and their terms, the deductible that applies, the waiting period and the verdict. Read from
 * `GET /v1/policies:coverageAtDate` at the page's `validAt`, so a member shows its group's override
 * as it stood on that day (sections the fleet changed are marked "Gruppentarif").
 */
export interface PolicyCoverageCardProps {
  readonly code: string;
  /** The page's cut: the date the coverage is read at (YYYY-MM-DD). */
  readonly validAt: string;
  readonly asOf?: string;
  readonly locale: string;
}

/** The verdicts, drawn with the platform's status tones. */
const VERDICT_TONE: Readonly<Record<string, string>> = {
  COVERED: 'ACTIVE',
  IN_WAITING_PERIOD: 'PENDING',
  NOT_IN_FORCE: 'TERMINATED',
  NO_SUCH_OBJECT: 'DRAFT',
};

const euros = (amount: number | undefined, currency: string, locale: string): string =>
  amount === undefined ? '—' : formatCurrency(Math.round(amount * 100), currency, locale);

export function PolicyCoverageCard({ code, validAt, asOf, locale }: PolicyCoverageCardProps): ReactNode {
  const { t } = useTranslation('insurance');
  const coverage = useCoverageAtDate({ policyCode: code, damageDate: validAt, ...(asOf === undefined ? {} : { asOf }) });

  if (coverage.isError) {
    return (
      <ErrorState
        description={t('policies.coverage.loadError')}
        details={coverage.error.message}
        onRetry={() => void coverage.refetch()}
      />
    );
  }
  if (coverage.isPending) return <Skeleton variant="rounded" height={180} data-testid="coverage-loading" />;

  const answer = coverage.data;
  const verdict = answer.verdict ?? 'COVERED';
  const detail = answer.coverage;
  const sources = answer.sources ?? {};
  const general = (answer.claimConfig as { generalSetting?: { currency?: string } } | undefined)?.generalSetting;
  const currency = general?.currency ?? 'EUR';
  const variantKey = answer.variant?.key ?? '';
  const fromGroup = (section: string): boolean => sources[`tariffVariations.${variantKey}.${section}`] === 'GROUP_TARIF';
  const groupMark = (marked: boolean): ReactNode =>
    marked ? <Chip size="small" variant="outlined" label={t('policies.coverage.fromGroup')} sx={{ ml: 1 }} /> : null;

  const termText = (term: InstanceApi.CoverageTermDetail | undefined): string => {
    if (term === undefined || term.isCovered === false) return t('policies.coverage.notCovered');
    const period = term.period === undefined || term.period === '' ? '' : t(`policies.coverage.period.${term.period}` as never, { defaultValue: '' });
    const value = term.value ?? {};
    const text =
      term.type === 'percent'
        ? value.limit === undefined
          ? t('policies.coverage.percent', { percent: new Intl.NumberFormat(locale, { style: 'percent' }).format((value.amount ?? 0) / 100) })
          : t('policies.coverage.percentUpTo', {
              percent: new Intl.NumberFormat(locale, { style: 'percent' }).format((value.amount ?? 0) / 100),
              limit: euros(value.limit, currency, locale),
            })
        : t('policies.coverage.fixed', { amount: euros(value.amount, currency, locale) });
    return period === '' ? text : `${text} ${period}`;
  };

  const waiting = detail?.waitingPeriod;
  const deductibles = detail?.deductibles ?? [];
  const limits = detail?.approvalLimits ?? [];
  const categories = (detail?.categories ?? []).filter((category) => category.offerable === true || category.isCovered !== true);

  return (
    <DetailsSection
      title={t('policies.coverage.title')}
      description={t('policies.coverage.asOf', { date: formatCivilDate(answer.validAt ?? validAt, locale) })}
      fields={[
        {
          label: t('policies.coverage.status'),
          value: (
            <StatusChip status={VERDICT_TONE[verdict] ?? 'DRAFT'} label={t(`policies.coverage.verdict.${verdict}` as never, { defaultValue: verdict })} />
          ),
        },
        { label: t('policies.coverage.variant'), value: answer.variant?.label ?? (variantKey === '' ? '—' : variantKey) },
        ...(detail === undefined
          ? []
          : [
              {
                label: t('policies.coverage.waiting'),
                value: (
                  <>
                    {waiting === undefined || (waiting.days ?? 0) === 0
                      ? t('policies.coverage.waitingNone')
                      : t('policies.coverage.waitingDays', {
                          days: waiting.days ?? 0,
                          date: formatCivilDate(waiting.endsOn ?? '', locale),
                        })}
                    {groupMark(fromGroup('waitingPeriod'))}
                  </>
                ),
              },
              {
                label: t('policies.coverage.deductibles'),
                value: (
                  <>
                    {deductibles.length === 0
                      ? '—'
                      : deductibles
                          .map((d) =>
                            t('policies.coverage.deductibleChosen', {
                              label: d.label ?? d.key ?? '',
                              amount: euros(d.chosenOption?.amount, currency, locale),
                              source: t(`policies.coverage.chosen.${d.chosenSource ?? 'DEFAULT_FIRST_OPTION'}` as never, { defaultValue: '' }),
                            }),
                          )
                          .join(' · ')}
                    {groupMark(fromGroup('deductibles'))}
                  </>
                ),
              },
              {
                label: t('policies.coverage.approvalLimits'),
                value: (
                  <>
                    {limits.length === 0
                      ? '—'
                      : limits.map((limit) => `${limit.roleCode ?? ''}: ${euros(limit.amount, currency, locale)}`).join(' · ')}
                    {groupMark(fromGroup('approvalLimits'))}
                  </>
                ),
              },
            ]),
        ...(answer.billing === undefined
          ? []
          : [
              {
                label: t('policies.coverage.billing'),
                value: t('policies.coverage.billingSummary', {
                  count: answer.billing.openInvoices ?? 0,
                  status: answer.billing.lastInvoiceStatus === undefined || answer.billing.lastInvoiceStatus === '' ? '—' : answer.billing.lastInvoiceStatus,
                }),
              },
            ]),
      ]}
      advancedFields={[
        { label: t('policies.coverage.document'), value: answer.tarifDeckungErn === '' ? '—' : answer.tarifDeckungErn },
        ...(answer.groupTarifDeckungErn === undefined || answer.groupTarifDeckungErn === ''
          ? []
          : [{ label: t('policies.coverage.groupDocument'), value: answer.groupTarifDeckungErn }]),
      ]}
    >
      {verdict === 'NOT_IN_FORCE' ? (
        <Stack sx={{ color: 'text.secondary', typography: 'body2' }}>{t('policies.coverage.notInForce', { date: formatCivilDate(validAt, locale) })}</Stack>
      ) : detail === undefined ? (
        <Stack sx={{ color: 'text.secondary', typography: 'body2' }}>{t('policies.coverage.noDocument')}</Stack>
      ) : (
        <Table size="small" aria-label={t('policies.coverage.categories')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('policies.coverage.category')}</TableCell>
              <TableCell>{t('policies.coverage.term')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.map((category) => (
              <TableRow key={category.key}>
                <TableCell>{category.label === '' ? category.key : category.label}</TableCell>
                <TableCell sx={category.isCovered === true ? undefined : { color: 'text.secondary' }}>
                  {termText(category.term)}
                  {groupMark(sources[`tariffVariations.${variantKey}.categories.${category.key ?? ''}`] === 'GROUP_TARIF')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </DetailsSection>
  );
}
